import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Viewer, Worker, SpecialZoomLevel } from '@react-pdf-viewer/core';
import { PDF_WORKER_URL } from '@/config/pdfViewer';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import invoiceService from '@/services/billing/invoice/invoiceService';
import { KeenIcon, DefaultTooltip } from '@/components'
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import { ActivityTab } from '@/components/activityLog';
import { useAbility } from '@/providers/';
import { Can } from '@casl/react';
import activityService from '@/services/activities/activityService';
import { toast } from 'sonner';
import { Container } from '@/components/container';

const InvoiceDetailsPreview = () => {
    const { ability } = useAbility();
    const { id } = useParams();
    const [blobUrl, setBlobUrl] = useState(null);
    const [downlaodPdfLoading, setDownlaodPdfLoading] = useState(false)
    const zoomPluginInstance = zoomPlugin();
    const { ZoomIn, ZoomOut, CurrentScale } = zoomPluginInstance;
    const [previewLoading, setPreviewLoading] = useState(false)
    const [activityData, setActivityData] = useState([]);
    const canViewActivity = ability.can('view', 'activity');

    useEffect(() => {
        const fetchPdf = async () => {
            setPreviewLoading(true)
            try {
                const response = await invoiceService.previewInvoice(id);
                const blob = new Blob([response], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                setBlobUrl(url);
            } catch (error) {
                console.error('Failed to fetch invoice PDF', error);
            } finally {
                setPreviewLoading(false)
            }
        };

        fetchPdf();
    }, [id]);

    const handleDwonlaod = async () => {
        setDownlaodPdfLoading(true)
        try {
            const blob = await invoiceService.previewInvoice(id, true);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `invoice.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to fetch invoice PDF', error);
            toast.error('Failed to dwonload.', {
                description: 'An error occurred while downloading invoice. Please try again later.',
            });
        } finally {
            setDownlaodPdfLoading(false)
        }
    }

    const getActivities = async () => {
        try {
            const payload = {
                model_id: id,
                model_type: 'invoice'
            }
            // const { data, status } = await activityService.getActivity(id);
            const { data, status } = await activityService.getActivity(payload);
            if (status) {
                setActivityData(data);
            }
        } catch (error) {
            console.error('Error', error)
            toast.error('Oops! Something went wrong.', {
                description: 'An error occurred while fetching Invoice Activities. Please try again in a moment.',
            });
        }
    }

    useEffect(() => {
        if (canViewActivity) { getActivities(); }
    }, [])

    return (
        <>
            <Container>
                <div className="card min-w-full mb-4">
                    <div className="card-header flex justify-between items-center">
                        <h3 className="card-title">Invoice Preview</h3>
                        <div className="flex items-center gap-2">
                            <button className='btn btn-sm btn-primary btn-outline' disabled={downlaodPdfLoading} onClick={handleDwonlaod}>
                                <KeenIcon icon="exit-up" />
                                {downlaodPdfLoading ? 'Please wait...' : 'Download PDF'}
                            </button>
                            <ZoomIn>
                                {(props) => (
                                    <button
                                        className="btn btn-xs btn-light"
                                        onClick={props.onClick}
                                        disabled={props.isDisabled}
                                    >
                                        <KeenIcon icon="plus" />
                                    </button>
                                )}
                            </ZoomIn>
                            <CurrentScale>
                                {(props) => (
                                    <span className="text-xs"> {Math.round(props.scale * 100)}%</span>
                                )}
                            </CurrentScale>
                            <ZoomOut>
                                {(props) => (
                                    <button
                                        className="btn btn-xs btn-light"
                                        onClick={props.onClick}
                                        disabled={props.isDisabled}
                                    >
                                        <KeenIcon icon="minus" />
                                    </button>
                                )}
                            </ZoomOut>

                        </div>
                    </div>

                    <div className="card-body">
                        {previewLoading ? (
                            <div className="text-center text-sm">Loading Preview...</div>
                        ) : blobUrl ? (
                            // Page width instead of a small actual-size page, capped like the Files viewer (OL-185).
                            <div className="max-w-6xl mx-auto">
                                <Worker workerUrl={PDF_WORKER_URL}>
                                    <Viewer fileUrl={blobUrl} plugins={[zoomPluginInstance]} defaultScale={SpecialZoomLevel.PageWidth} />
                                </Worker>
                            </div>
                        ) : (
                            <div className="text-center text-sm text-red-500">No preview available</div>
                        )}
                    </div>
                </div>
                <Can I="view" a="activity" ability={ability}>
                    <ActivityTab activityData={activityData} />
                </Can>
            </Container>
        </>
    );
};

export { InvoiceDetailsPreview };
